import { Button, Form, Input, message } from "antd";
import axios from "axios";
import React, { useEffect, useState } from "react";
import { useCookies } from "react-cookie";
import { useNavigate, Link } from "react-router-dom";
import { GoogleLogin, GoogleOAuthProvider } from '@react-oauth/google';
import { LockOutlined, MailOutlined, FacebookFilled } from '@ant-design/icons';
import logo from '../component/image/logo.png';

const Login = () => {
  const navigate = useNavigate();
  const [, setCookie] = useCookies(["user"]);
  const [, setCookieToken] = useCookies(["accessToken"]);
  const [fbSDKReady, setFbSDKReady] = useState(false);

  // Initialize Facebook SDK
  useEffect(() => {
    console.log('Initializing Facebook SDK...');
    console.log('Facebook App ID:', process.env.REACT_APP_FACEBOOK_APP_ID);
    
    window.fbAsyncInit = function() {
      console.log('Facebook SDK loaded, initializing...');
      window.FB.init({
        appId: process.env.REACT_APP_FACEBOOK_APP_ID,
        cookie: true,
        xfbml: true,
        version: 'v18.0'
      });
      
      window.FB.getLoginStatus(function(response) {
        console.log('Facebook login status:', response);
        setFbSDKReady(true);
        console.log('Facebook SDK is ready!');
      });
    };

    // Load Facebook SDK script
    if (!document.getElementById('facebook-jssdk')) {
      console.log('Loading Facebook SDK script...');
      const script = document.createElement('script');
      script.id = 'facebook-jssdk';
      script.src = 'https://connect.facebook.net/vi_VN/sdk.js';
      script.async = true;
      script.defer = true;
      script.crossOrigin = 'anonymous';
      document.body.appendChild(script);
    } else if (window.FB) {
      setFbSDKReady(true);
    }
  }, []);

  const loginUser = async (values) => {
    try {
      const res = await axios.post(`${process.env.REACT_APP_API_URL}/user/login`, values);
      setCookie("user", res?.data);
      setCookieToken("accessToken", res?.data?.token);
      message.success("Đăng nhập thành công!");
      navigate("/");
    } catch (err) {
      message.error("Tài khoản hoặc mật khẩu không đúng!");
    }
  };

  const onFinishFailed = () => {
    message.error("Bạn đang điền thiếu thông tin!");
  };

  const loginGmail = async (credentialResponse) => {
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/auth/login`,
        { googleId: credentialResponse?.credential },
        { headers: { Accept: 'application/json', 'Content-Type': 'application/json' } }
      );
      setCookie("user", res?.data);
      message.success("Đăng nhập thành công!");
      navigate("/");
    } catch (err) {
      message.error("Tài khoản hoặc mật khẩu không đúng!");
    }
  };

  const loginFacebook = () => {
    console.log('Login Facebook button clicked');
    console.log('FB SDK Ready:', fbSDKReady);
    console.log('window.FB exists:', !!window.FB);
    
    // Check if Facebook SDK is loaded
    if (!window.FB) {
      message.error("Facebook SDK chưa sẵn sàng. Vui lòng thử lại sau giây lát!");
      console.error('Facebook SDK not loaded!');
      return;
    }

    console.log('Calling FB.login...');
    
    try {
      // Call Facebook login popup
      window.FB.login((response) => {
        console.log('Facebook login response:', response);
        
        if (response.authResponse) {
          const accessToken = response.authResponse.accessToken;
          console.log('Got access token:', accessToken.substring(0, 20) + '...');
          
          // Show loading message
          message.loading({ content: "Đang xác thực với Facebook...", key: "fbLogin" });
          
          // Send access token to backend
          console.log('Sending token to backend:', process.env.REACT_APP_API_URL + '/auth/facebook');
          
          axios.post(
            `${process.env.REACT_APP_API_URL}/auth/facebook`,
            { accessToken },
            { headers: { Accept: 'application/json', 'Content-Type': 'application/json' } }
          )
          .then(res => {
            console.log('Backend response:', res.data);
            message.destroy("fbLogin");
            setCookie("user", res?.data);
            setCookieToken("accessToken", res?.data?.token);
            message.success("Đăng nhập Facebook thành công!");
            navigate("/");
          })
          .catch(err => {
            message.destroy("fbLogin");
            console.error('Backend error:', err);
            console.error('Error response:', err.response?.data);
            const errorMsg = err.response?.data?.message || "Đăng nhập Facebook thất bại!";
            message.error(errorMsg);
          });
        } else {
          // User cancelled login or did not fully authorize
          console.log('User cancelled or denied Facebook login');
          message.warning("Bạn đã hủy đăng nhập Facebook");
        }
      }, { scope: 'public_profile,email' });
    } catch (error) {
      console.error('Facebook login error:', error);
      message.error("Có lỗi xảy ra khi đăng nhập Facebook!");
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Panel - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-[#0e4a61] via-[#1565a0] to-[#1e90ff] relative overflow-hidden items-center justify-center">
        {/* Decorative circles */}
        <div className="absolute top-20 left-20 w-72 h-72 bg-white/5 rounded-full"></div>
        <div className="absolute bottom-10 right-10 w-96 h-96 bg-white/5 rounded-full"></div>
        <div className="absolute top-1/2 left-1/3 w-40 h-40 bg-white/5 rounded-full"></div>

        <div className="relative z-10 text-center text-white px-12 max-w-lg">
          <img src={logo} alt="Logo" className="w-28 h-28 mx-auto mb-8 drop-shadow-2xl" />
          <h1 className="text-4xl font-extrabold mb-4">Effortless English</h1>
          <p className="text-blue-100 text-lg mb-8">Phương pháp học tiếng Anh tự nhiên, giúp bạn nói tiếng Anh tự tin chỉ sau vài tháng</p>
          <div className="flex justify-center gap-6">
            <div className="text-center">
              <div className="text-3xl font-extrabold">10K+</div>
              <div className="text-blue-200 text-sm">Học viên</div>
            </div>
            <div className="w-px bg-white/20"></div>
            <div className="text-center">
              <div className="text-3xl font-extrabold">100+</div>
              <div className="text-blue-200 text-sm">Bài học</div>
            </div>
            <div className="w-px bg-white/20"></div>
            <div className="text-center">
              <div className="text-3xl font-extrabold">4.8★</div>
              <div className="text-blue-200 text-sm">Đánh giá</div>
            </div>
          </div>
        </div>
      </div>

      {/* Right Panel - Login Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center bg-[#0a1628] px-6 py-12">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="lg:hidden text-center mb-8">
            <img src={logo} alt="Logo" className="w-20 h-20 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-white">Effortless English</h2>
          </div>

          {/* Form Card */}
          <div className="bg-[#0f1e35] border border-white/10 rounded-2xl p-8 md:p-10">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-extrabold text-white mb-2">Đăng Nhập</h2>
              <p className="text-gray-400">Chào mừng bạn quay trở lại!</p>
            </div>

            <Form
              onFinish={loginUser}
              onFinishFailed={onFinishFailed}
              name="login"
              layout="vertical"
              size="large"
            >
              <Form.Item
                name="email"
                rules={[{ required: true, message: 'Vui lòng nhập email!' }]}
              >
                <Input
                  prefix={<MailOutlined className="text-gray-400 mr-2" />}
                  placeholder="Email"
                  className="!rounded-xl !py-3 !px-4 !bg-[#0a1628] !border-white/10 !text-white hover:!border-blue-400 focus:!border-blue-500 [&_.ant-input-prefix]:text-gray-400"
                />
              </Form.Item>

              <Form.Item
                name="password"
                rules={[{ required: true, message: 'Vui lòng nhập mật khẩu!' }]}
              >
                <Input.Password
                  prefix={<LockOutlined className="text-gray-400 mr-2" />}
                  placeholder="Mật khẩu"
                  className="!rounded-xl !py-3 !px-4 !bg-[#0a1628] !border-white/10 !text-white hover:!border-blue-400 focus:!border-blue-500"
                />
              </Form.Item>

              <Button
                type="primary"
                htmlType="submit"
                block
                className="!h-12 !rounded-xl !bg-gradient-to-r !from-blue-500 !to-blue-700 !border-none !text-lg !font-semibold hover:!from-blue-600 hover:!to-blue-800 !shadow-lg !shadow-blue-500/25"
              >
                Đăng nhập
              </Button>
            </Form>

            {/* Divider */}
            <div className="flex items-center my-6">
              <div className="flex-1 h-px bg-white/10"></div>
              <span className="px-4 text-sm text-gray-500">hoặc</span>
              <div className="flex-1 h-px bg-white/10"></div>
            </div>

            {/* Google Login */}
            <div className="flex justify-center mb-3">
              <GoogleOAuthProvider clientId="253239939416-cv56na10g3t9trr8jaij772bv2frh7u0.apps.googleusercontent.com">
                <GoogleLogin
                  onSuccess={loginGmail}
                  onError={(err) => console.log(err)}
                  shape="pill"
                  theme="outline"
                  size="large"
                  width="360"
                  text="signin_with"
                  locale="vi"
                />
              </GoogleOAuthProvider>
            </div>

            {/* Facebook Login */}
            <button
              onClick={loginFacebook}
              disabled={!fbSDKReady}
              className={`w-full flex items-center justify-center gap-3 px-6 py-3 rounded-xl font-semibold transition-all shadow-md ${
                fbSDKReady 
                  ? 'bg-[#1877f2] hover:bg-[#166fe5] hover:shadow-lg cursor-pointer' 
                  : 'bg-gray-600 cursor-not-allowed opacity-60'
              } text-white`}
            >
              <FacebookFilled className="text-xl" />
              <span>{fbSDKReady ? 'Đăng nhập với Facebook' : 'Đang tải Facebook SDK...'}</span>
            </button>

            {/* Footer Links */}
            <div className="mt-8 text-center">
              <p className="text-gray-500 text-sm">
                Chưa có tài khoản?{' '}
                <Link to="/contact" className="text-blue-400 font-semibold hover:text-blue-300">
                  Liên hệ đăng ký
                </Link>
              </p>
            </div>
          </div>

          {/* Back to home */}
          <div className="text-center mt-6">
            <Link to="/" className="text-gray-500 hover:text-gray-300 text-sm transition-colors">
              ← Quay về trang chủ
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
